import { faCheck, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useCallback, useEffect, useState } from 'react'
import { useSearch } from '../../contexts/CityFilterContext'
import {
  CategoryDto,
  CityDto,
  fetchCategories,
  fetchCityStateCombinations,
} from '../../data/location.service'
import { COLORS } from '../../utils/constants'
import {
  FilterState,
  getFootTrafficSummary,
  getPriceSummary,
  getScreenSizeSummary,
  hasActiveFilters,
  isFootTrafficDefault,
  isPriceDefault,
  isScreenSizeDefault,
} from '../../utils/filterUtils'
import FootTrafficSelector from './FootTrafficSelector'
import RangeSelector from './RangeSelector'

interface FilterSectionProps {
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
  triggerSearchWithFilters: (
    newFilters: FilterState,
    cityFilter?: string | null,
  ) => void
  businessNameSearch: string
  setBusinessNameSearch: (value: string) => void
  onCityFilterChange?: (city: string | null) => void
  onCitiesLoaded?: (cities: CityDto[]) => void
}

const PRICE_MIN = 0
const PRICE_MAX = 1000
const SCREEN_MIN = 0
const SCREEN_MAX = 200

const footTrafficOptions = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'steady', label: 'Steady' },
  { value: 'busy', label: 'Busy' },
  { value: 'high', label: 'High' },
]

function FilterSection({
  filters,
  onFiltersChange,
  triggerSearchWithFilters,
  businessNameSearch,
  setBusinessNameSearch,
  onCityFilterChange,
  onCitiesLoaded,
}: FilterSectionProps) {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const { selectedCityFilter } = useSearch()

  // Local state for popover editing
  const [pricePopoverOpen, setPricePopoverOpen] = useState(false)
  const [screenPopoverOpen, setScreenPopoverOpen] = useState(false)
  const [footTrafficPopoverOpen, setFootTrafficPopoverOpen] = useState(false)

  // Local state for mobile filters (to avoid immediate HTTP calls)
  const [mobileFilters, setMobileFilters] = useState<FilterState>(filters)
  const [mobileBusinessNameSearch, setMobileBusinessNameSearch] =
    useState(businessNameSearch)
  const [mobileCityFilter, setMobileCityFilter] = useState(selectedCityFilter)
  const [cityOptions, setCityOptions] = useState<CityDto[]>([])
  const [categoryOptions, setCategoryOptions] = useState<CategoryDto[]>([])

  // Function to count active filters
  const getActiveFilterCount = (
    filterState: FilterState,
    businessName: string,
    cityFilter: string | null,
  ) => {
    let count = 0
    if (!isPriceDefault(filterState.priceRange)) count++
    if (!isScreenSizeDefault(filterState.screenSizeRange)) count++
    if (filterState.category != null) count++
    if (filterState.autoApprovalOnly) count++
    if (filterState.footTraffic.length > 0) count++
    if (businessName.trim().length > 0) count++
    return count
  }

  useEffect(() => {
    const fetchCityOptions = async () => {
      try {
        const cities = await fetchCityStateCombinations()
        setCityOptions(cities)
        if (onCitiesLoaded) {
          onCitiesLoaded(cities)
        }
      } catch (error) {
        console.error('Error fetching city options:', error)
      }
    }

    const fetchCategoryOptions = async () => {
      try {
        const categories = await fetchCategories()
        setCategoryOptions(categories)
      } catch (error) {
        console.error('Error fetching category options:', error)
      }
    }

    fetchCityOptions()
    fetchCategoryOptions()
  }, [])

  const handleFilterChange = useCallback(
    (key: keyof FilterState, value: any) => {
      if (isMobile) {
        // On mobile, update local state only
        setMobileFilters((prev) => ({
          ...prev,
          [key]: value,
          currentPage: 1,
        }))
      } else {
        // On desktop, update immediately
        onFiltersChange({
          ...filters,
          [key]: value,
          currentPage: 1,
        })
      }
    },
    [filters, onFiltersChange, isMobile],
  )

  // Filter summaries and active checks
  const priceSummary = getPriceSummary(filters.priceRange)
  const screenSummary = getScreenSizeSummary(filters.screenSizeRange)

  // Auto-approval pill logic
  const autoApprovalActive = filters.autoApprovalOnly

  // Button color logic
  const getPillStyle = (active: boolean) => ({
    borderRadius: 30,
    marginRight: 8,
    color: active ? 'white' : COLORS.LightGreen,
    borderColor: COLORS.LightGreen,
    background: active ? COLORS.LightGreen : 'white',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
  })

  // Check if any filters are active (excluding city filter)
  const hasActiveFiltersValue = hasActiveFilters({
    ...filters,
    businessName: businessNameSearch,
  })

  const FilterContent = () => (
    <Group
      gap={8}
      wrap='nowrap'
      align='center'
      style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
    >
      {/* Price filter */}
      <RangeSelector
        title='Price per month'
        min={PRICE_MIN}
        max={PRICE_MAX}
        value={filters.priceRange}
        onChange={(value) => handleFilterChange('priceRange', value)}
        opened={pricePopoverOpen}
        onOpenedChange={(opened, newValue) => {
          setPricePopoverOpen(opened)
          // Trigger search when popover closes and value actually changed
          if (!opened && newValue && !isPriceDefault(newValue)) {
            const updatedFilters = {
              ...filters,
              priceRange: newValue,
              businessName: businessNameSearch,
              currentPage: 1,
            }
            triggerSearchWithFilters(updatedFilters)
          }
        }}
        summary={priceSummary}
        isDefault={isPriceDefault(filters.priceRange)}
        onClear={() => {
          const updatedFilters = {
            ...filters,
            priceRange: [null, null] as [number | null, number | null],
            businessName: businessNameSearch,
            currentPage: 1,
          }
          handleFilterChange('priceRange', [null, null])
          triggerSearchWithFilters(updatedFilters)
        }}
        prefix='$'
        label={(value) => `$${value}`}
      />

      {/* Screen size filter */}
      <RangeSelector
        title='Screen size (inches)'
        min={SCREEN_MIN}
        max={SCREEN_MAX}
        value={filters.screenSizeRange}
        onChange={(value) => handleFilterChange('screenSizeRange', value)}
        opened={screenPopoverOpen}
        onOpenedChange={(opened, newValue) => {
          setScreenPopoverOpen(opened)
          // Trigger search when popover closes and value actually changed
          if (!opened && newValue && !isScreenSizeDefault(newValue)) {
            const updatedFilters = {
              ...filters,
              screenSizeRange: newValue,
              businessName: businessNameSearch,
              currentPage: 1,
            }
            triggerSearchWithFilters(updatedFilters)
          }
        }}
        summary={screenSummary}
        isDefault={isScreenSizeDefault(filters.screenSizeRange)}
        onClear={() => {
          const updatedFilters = {
            ...filters,
            screenSizeRange: [null, null] as [number | null, number | null],
            businessName: businessNameSearch,
            currentPage: 1,
          }
          handleFilterChange('screenSizeRange', [null, null])
          triggerSearchWithFilters(updatedFilters)
        }}
        suffix='"'
        label={(value) => `${value}"`}
      />

      {/* Category filter */}
      <Select
        placeholder='Category'
        value={filters.category?.toString()}
        onChange={(value: string | null) => {
          const newCategory = value ? parseInt(value) : null
          const updatedFilters = {
            ...filters,
            category: newCategory,
            businessName: businessNameSearch,
            currentPage: 1,
          }
          handleFilterChange('category', newCategory)
          triggerSearchWithFilters(updatedFilters)
        }}
        data={[
          ...categoryOptions
            .map((cat) => ({
              value: cat.id.toString(),
              label: cat.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ]}
        size='sm'
        radius='xl'
        styles={{
          input: {
            ...getPillStyle(filters.category != null),
            padding: '0 20px',
            minWidth: 120,
            cursor: 'pointer',
          },
        }}
        mr={8}
        rightSection={
          filters.category != null ? (
            <FontAwesomeIcon
              icon={faTimes}
              style={{ cursor: 'pointer', color: 'white' }}
              onClick={(e) => {
                e.stopPropagation()
                const updatedFilters = {
                  ...filters,
                  category: null,
                  businessName: businessNameSearch,
                  currentPage: 1,
                }
                handleFilterChange('category', null)
                triggerSearchWithFilters(updatedFilters)
              }}
            />
          ) : undefined
        }
        clearable
        searchable
      />

      {/* Foot Traffic filter */}
      <FootTrafficSelector
        selectedValues={filters.footTraffic}
        onChange={(values: string[]) => {
          const updatedFilters = {
            ...filters,
            footTraffic: values,
            businessName: businessNameSearch,
            currentPage: 1,
          }
          handleFilterChange('footTraffic', values)
          triggerSearchWithFilters(updatedFilters)
        }}
        opened={footTrafficPopoverOpen}
        onOpenedChange={setFootTrafficPopoverOpen}
        summary={getFootTrafficSummary(filters.footTraffic)}
        isDefault={isFootTrafficDefault(filters.footTraffic)}
        onClear={() => {
          const updatedFilters = {
            ...filters,
            footTraffic: [],
            businessName: businessNameSearch,
            currentPage: 1,
          }
          handleFilterChange('footTraffic', [])
          triggerSearchWithFilters(updatedFilters)
        }}
      />

      {/* Auto-approval pill */}
      <Button
        variant='outline'
        radius='xl'
        pl={20}
        pr={12}
        style={getPillStyle(autoApprovalActive)}
        onClick={() => {
          const newAutoApproval = !autoApprovalActive
          const updatedFilters = {
            ...filters,
            autoApprovalOnly: newAutoApproval,
            businessName: businessNameSearch,
            currentPage: 1,
          }
          handleFilterChange('autoApprovalOnly', newAutoApproval)
          triggerSearchWithFilters(updatedFilters)
        }}
      >
        {autoApprovalActive && (
          <FontAwesomeIcon
            icon={faCheck}
            style={{ marginRight: 8, color: 'white' }}
          />
        )}
        Auto-approval Only
        {autoApprovalActive && (
          <FontAwesomeIcon
            icon={faTimes}
            style={{ marginLeft: 8, color: 'white', fontSize: 16 }}
            onClick={(e) => {
              e.stopPropagation()
              const updatedFilters = {
                ...filters,
                autoApprovalOnly: false,
                businessName: businessNameSearch,
                currentPage: 1,
              }
              handleFilterChange('autoApprovalOnly', false)
              triggerSearchWithFilters(updatedFilters)
            }}
          />
        )}
      </Button>
    </Group>
  )

  if (isMobile) {
    return (
      <>
        {/* Mobile filter button */}
        <Group gap={8} align='center' style={{ width: '100%' }}>
          {/* City filter for mobile */}
          <Select
            placeholder='City'
            value={selectedCityFilter}
            onChange={(value: string | null) => {
              if (onCityFilterChange) {
                onCityFilterChange(value)
              }
              // Trigger search with updated city filter
              const updatedFilters = {
                ...filters,
                businessName: businessNameSearch,
                currentPage: 1,
              }
              triggerSearchWithFilters(updatedFilters, value)
            }}
            data={[
              { label: 'Anywhere', value: '' },
              ...cityOptions
                .map((city) => ({
                  label: `${city.name}, ${city.state}`,
                  value: `${city.name}, ${city.state}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            ]}
            size='sm'
            radius='xl'
            style={{ flex: 1 }}
            styles={{
              input: {
                border: `1px solid ${COLORS.LightGreen}`,
                borderRadius: 30,
                padding: '0 16px',
                cursor: 'pointer',
                fontSize: 16,
              },
              dropdown: {
                zIndex: 1001,
              },
            }}
            clearable
            searchable
            allowDeselect={false}
          />

          <Box
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
          >
            <ActionIcon
              size='lg'
              variant={hasActiveFiltersValue ? 'filled' : 'outline'}
              color={COLORS.LightGreen}
              onClick={() => {
                // Sync mobile filters with current filters when opening
                setMobileFilters(filters)
                setMobileBusinessNameSearch(businessNameSearch)
                setMobileCityFilter(selectedCityFilter)
                setMobileFiltersOpen(true)
              }}
              style={{
                borderRadius: 8,
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
            </ActionIcon>
            {(() => {
              const activeCount = getActiveFilterCount(
                filters,
                businessNameSearch,
                selectedCityFilter,
              )
              return activeCount > 0 ? (
                <Box
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    backgroundColor: COLORS.HiddenGemzRed,
                    color: 'white',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 'bold',
                    zIndex: 10,
                  }}
                >
                  {activeCount}
                </Box>
              ) : null
            })()}
          </Box>
        </Group>

        {/* Mobile filter drawer */}
        <Drawer
          opened={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          position='right'
          size='100%'
          zIndex={1000}
          title='Filters'
          styles={{
            header: { backgroundColor: COLORS.LightGreen, color: 'white' },
            title: { color: 'white' },
            close: {
              color: 'white',
              width: 32,
              height: 32,
            },
          }}
        >
          <Box
            p='md'
            style={{
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 0,
            }}
          >
            {/* Clear All button at top */}
            <Box style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button
                variant='subtle'
                size='xs'
                style={{
                  color: COLORS.LightGreen,
                  padding: 0,
                  height: 'auto',
                  fontSize: 12,
                }}
                onClick={() => {
                  // Clear all filters (except city filter)
                  setMobileFilters({
                    category: null,
                    autoApprovalOnly: false,
                    priceRange: [null, null],
                    businessName: '',
                    screenSizeRange: [null, null],
                    footTraffic: [],
                    currentPage: 1,
                  })
                  setMobileBusinessNameSearch('')
                }}
              >
                Clear All
              </Button>
            </Box>

            {/* Business name filter for mobile */}
            <Box style={{ marginBottom: 16 }}>
              <Text size='sm' fw={500} mb={8}>
                Business Name
              </Text>
              <TextInput
                value={mobileBusinessNameSearch}
                onChange={(e) =>
                  setMobileBusinessNameSearch(e.currentTarget.value)
                }
                placeholder='Search business name...'
                size='md'
                radius='xl'
                style={{ width: '100%' }}
              />
            </Box>

            <Box style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
              {/* Mobile filter content using mobile state */}
              <Stack gap={16} style={{ width: '100%' }}>
                {/* Price filter */}
                <Box style={{ width: '100%' }}>
                  <Text size='sm' fw={500} mb={8}>
                    Price per month
                  </Text>
                  <Group gap={8} style={{ width: '100%' }}>
                    <TextInput
                      placeholder='Min'
                      value={mobileFilters.priceRange[0]?.toString() || ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value.replace('$', '')
                        const numValue = value === '' ? null : parseFloat(value)
                        setMobileFilters((prev) => ({
                          ...prev,
                          priceRange: [numValue, prev.priceRange[1]],
                          currentPage: 1,
                        }))
                      }}
                      style={{ flex: 1 }}
                      radius='xl'
                      size='md'
                      leftSection='$'
                    />
                    <Text size='sm' c='dimmed'>
                      to
                    </Text>
                    <TextInput
                      placeholder='Max'
                      value={mobileFilters.priceRange[1]?.toString() || ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value.replace('$', '')
                        const numValue = value === '' ? null : parseFloat(value)
                        setMobileFilters((prev) => ({
                          ...prev,
                          priceRange: [prev.priceRange[0], numValue],
                          currentPage: 1,
                        }))
                      }}
                      style={{ flex: 1 }}
                      radius='xl'
                      size='md'
                      leftSection='$'
                    />
                  </Group>
                </Box>

                {/* Screen size filter */}
                <Box style={{ width: '100%' }}>
                  <Text size='sm' fw={500} mb={8}>
                    Screen size (inches)
                  </Text>
                  <Group gap={8} style={{ width: '100%' }}>
                    <TextInput
                      placeholder='Min'
                      value={mobileFilters.screenSizeRange[0]?.toString() || ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value.replace('"', '')
                        const numValue = value === '' ? null : parseFloat(value)
                        setMobileFilters((prev) => ({
                          ...prev,
                          screenSizeRange: [numValue, prev.screenSizeRange[1]],
                          currentPage: 1,
                        }))
                      }}
                      style={{ flex: 1 }}
                      radius='xl'
                      size='md'
                      rightSection='"'
                    />
                    <Text size='sm' c='dimmed'>
                      to
                    </Text>
                    <TextInput
                      placeholder='Max'
                      value={mobileFilters.screenSizeRange[1]?.toString() || ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value.replace('"', '')
                        const numValue = value === '' ? null : parseFloat(value)
                        setMobileFilters((prev) => ({
                          ...prev,
                          screenSizeRange: [prev.screenSizeRange[0], numValue],
                          currentPage: 1,
                        }))
                      }}
                      style={{ flex: 1 }}
                      radius='xl'
                      size='md'
                      rightSection='"'
                    />
                  </Group>
                </Box>

                {/* Category filter */}
                <Box style={{ width: '100%' }}>
                  <Text size='sm' fw={500} mb={8}>
                    Category
                  </Text>
                  <Select
                    placeholder='Select category'
                    value={mobileFilters.category?.toString()}
                    onChange={(value: string | null) => {
                      const newCategory = value ? parseInt(value) : null
                      setMobileFilters((prev) => ({
                        ...prev,
                        category: newCategory,
                        currentPage: 1,
                      }))
                    }}
                    data={[
                      ...categoryOptions
                        .map((cat) => ({
                          value: cat.id.toString(),
                          label: cat.name,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),
                    ]}
                    size='md'
                    radius='xl'
                    style={{ width: '100%' }}
                    clearable
                    searchable
                    allowDeselect={false}
                    styles={{
                      dropdown: {
                        zIndex: 1001,
                      },
                    }}
                  />
                </Box>

                {/* Foot Traffic filter */}
                <Box style={{ width: '100%' }}>
                  <Text size='sm' fw={500} mb={8}>
                    Foot Traffic
                  </Text>
                  <Stack gap={8}>
                    {footTrafficOptions.map((option) => (
                      <Checkbox
                        key={option.value}
                        label={option.label}
                        checked={mobileFilters.footTraffic.includes(
                          option.value,
                        )}
                        onChange={() => {
                          const newValues = mobileFilters.footTraffic.includes(
                            option.value,
                          )
                            ? mobileFilters.footTraffic.filter(
                                (v) => v !== option.value,
                              )
                            : [...mobileFilters.footTraffic, option.value]
                          setMobileFilters((prev) => ({
                            ...prev,
                            footTraffic: newValues,
                            currentPage: 1,
                          }))
                        }}
                        styles={{
                          label: {
                            cursor: 'pointer',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                {/* Auto-approval pill */}
                <Box style={{ width: '100%' }}>
                  <Text size='sm' fw={500} mb={8}>
                    Auto-approval Only
                  </Text>
                  <Box
                    style={{
                      ...getPillStyle(mobileFilters.autoApprovalOnly),
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 20px',
                      cursor: 'pointer',
                      borderRadius: 30,
                      border: mobileFilters.autoApprovalOnly
                        ? `1px solid ${COLORS.LightGreen}`
                        : '1px solid #ced4da',
                      background: mobileFilters.autoApprovalOnly
                        ? COLORS.LightGreen
                        : 'white',
                      color: mobileFilters.autoApprovalOnly
                        ? 'white'
                        : COLORS.LightGreen,
                    }}
                    onClick={() => {
                      const newAutoApproval = !mobileFilters.autoApprovalOnly
                      setMobileFilters((prev) => ({
                        ...prev,
                        autoApprovalOnly: newAutoApproval,
                        currentPage: 1,
                      }))
                    }}
                  >
                    <Group gap={8} align='center'>
                      {mobileFilters.autoApprovalOnly && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          style={{ color: 'white' }}
                        />
                      )}
                      <span>Auto-approval Only</span>
                    </Group>
                    {mobileFilters.autoApprovalOnly && (
                      <FontAwesomeIcon
                        icon={faTimes}
                        style={{ color: 'white', fontSize: 16 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setMobileFilters((prev) => ({
                            ...prev,
                            autoApprovalOnly: false,
                            currentPage: 1,
                          }))
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Stack>
            </Box>
            {/* Find Locations button at bottom */}
            <Box
              style={{
                padding: '16px 0',
                borderTop: '1px solid #e0e0e0',
                backgroundColor: 'white',
                position: 'sticky',
                bottom: 0,
              }}
            >
              <Button
                fullWidth
                size='lg'
                style={{
                  backgroundColor: COLORS.LightGreen,
                  color: 'white',
                  borderRadius: 8,
                }}
                onClick={() => {
                  // Apply mobile filters and close drawer
                  const updatedFilters = {
                    ...mobileFilters,
                    businessName: mobileBusinessNameSearch,
                    currentPage: 1,
                  }
                  onFiltersChange(updatedFilters)
                  setBusinessNameSearch(mobileBusinessNameSearch)

                  // Update city filter if changed
                  if (
                    onCityFilterChange &&
                    mobileCityFilter !== selectedCityFilter
                  ) {
                    onCityFilterChange(mobileCityFilter)
                  }

                  // Trigger search with updated filters and city filter
                  triggerSearchWithFilters(updatedFilters, mobileCityFilter)
                  setMobileFiltersOpen(false)
                }}
              >
                Find Locations
              </Button>
            </Box>
          </Box>
        </Drawer>
      </>
    )
  }

  return <FilterContent />
}

export default FilterSection
